/**
 * Disposal utilities — safe cleanup of Three.js GPU resources.
 *
 * Three.js does not automatically free GPU memory when objects go out of
 * scope. Every geometry, material, and texture must be explicitly disposed.
 * These utilities encapsulate the traversal + disposal pattern shared by
 * all scene implementations.
 *
 * Usage:
 *   const THREE = await import('three');
 *   disposeMeshTree(THREE, gltf.scene);  // full subtree cleanup
 *   disposeTextures(material);            // single material's textures
 */

import type { Mesh, Object3D, Material } from 'three';
import type { ThreeModule } from './types.js';

/**
 * Texture map property names on MeshStandardMaterial / MeshPhysicalMaterial.
 *
 * Each of these may hold a Texture that needs explicit disposal.
 */
const TEXTURE_SLOTS: readonly string[] = [
  'map',
  'normalMap',
  'metalnessMap',
  'roughnessMap',
  'bumpMap',
  'alphaMap',
  'aoMap',
  'emissiveMap',
] as const;

/**
 * Dispose all textures attached to a material.
 *
 * Iterates over the 8 standard PBR texture slots and calls dispose()
 * on each one that exists. Safe to call on any material type — unknown
 * properties are silently skipped.
 *
 * @param material - A Three.js Material (typically MeshStandardMaterial).
 */
export function disposeTextures(material: Material): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mat = material as any;
  for (const slot of TEXTURE_SLOTS) {
    const tex = mat[slot];
    if (tex && typeof tex.dispose === 'function') {
      tex.dispose();
    }
  }
}

/**
 * Traverse a scene subtree and dispose all geometry + material + texture
 * GPU resources.
 *
 * This is the "nuclear option" for cleanup — use it when tearing down an
 * entire scene or unloading a model. Does NOT remove objects from the
 * scene graph (call parent.remove() separately if needed).
 *
 * @param THREE - Dynamically imported Three.js module (needs Mesh constructor).
 * @param root  - Root of the subtree to dispose (e.g. gltf.scene or scene itself).
 */
export function disposeMeshTree(THREE: ThreeModule, root: Object3D): void {
  root.traverse((obj: Object3D) => {
    if (!(obj instanceof (THREE.Mesh as unknown as typeof Mesh))) return;
    const mesh = obj as Mesh;

    // Dispose geometry
    mesh.geometry?.dispose();

    // Dispose materials (handle both single and multi-material)
    if (Array.isArray(mesh.material)) {
      for (const mat of mesh.material as Material[]) {
        disposeTextures(mat);
        mat.dispose();
      }
    } else if (mesh.material) {
      const mat = mesh.material as Material;
      disposeTextures(mat);
      mat.dispose();
    }
  });
}
